/**
 * Los errores y avisos de identificadores en los tres idiomas del cliente.
 *
 * El castellano es el mensaje de la regla (isrc.ts, iswc.ts, ipi.ts), con los
 * datos del caso. Acá están el inglés y el portugués, uno por código. Los datos
 * del caso (el código de agencia, el prefijo, la sugerencia del ISWC, la
 * cantidad de dígitos) se toman del mensaje en castellano; si no están, la
 * frase va sin ellos. Un código sin traducción devuelve el castellano.
 *
 * Lo usan el campo del cliente y el server (al guardar y en el chequeo previo a
 * generar): el mismo problema se lee igual lo rechace quien lo rechace.
 *
 * ESPEJO byte-idéntico: jupiter-server/src/common/validation/identifiers/identifierMessages.ts
 * y jupiter-client/src/entities/common/validation/identifiers/identifierMessages.ts
 */

import type { IdentifierIssue } from "./index";
import { IFPI_AGENCY_CODES_DATE } from "./ifpiAgencyCodes";

export type IdentifierLanguage = "es" | "en" | "pt";

type Translate = (spanish: string) => string;

/** El primer grupo de la regex en el mensaje en castellano, o undefined. */
function detail(spanish: string, pattern: RegExp): string | undefined {
  return pattern.exec(spanish)?.[1];
}

/** La sugerencia del ISWC ("¿Quisiste decir T-034.524.680-1?"), si la hay. */
function iswcSuggestion(spanish: string, ask: string): string {
  const suggested = detail(spanish, /¿Quisiste decir (T-[0-9.-]+)\?/);
  return suggested ? ` ${ask} ${suggested}?` : "";
}

/** Qué letras del ISRC parecen números: la O, la I o las dos. */
function lettersForDigits(spanish: string, o: string, i: string, and: string): string {
  const parts = [
    ...(spanish.includes("la O es un cero") ? [o] : []),
    ...(spanish.includes("la I es un uno") ? [i] : []),
  ];
  return (parts.length > 0 ? parts : [o, i]).join(` ${and} `);
}

const TEXTS: Record<string, Record<"en" | "pt", Translate>> = {
  ISRC_FORMAT: {
    en: () =>
      "An ISRC has 12 characters: 2 letters, 3 letters or digits and 7 digits (for example AR-F03-97-03368).",
    pt: () =>
      "O ISRC tem 12 caracteres: 2 letras, 3 letras ou números e 7 números (por exemplo AR-F03-97-03368).",
  },
  ISRC_MISSING_DIGIT: {
    en: () =>
      "It has 11 characters and an ISRC has 12: is a zero missing? The final number always has 5 digits (00047, not 47).",
    pt: () =>
      "Tem 11 caracteres e o ISRC tem 12: falta um zero? O número final sempre tem 5 dígitos (00047, não 47).",
  },
  ISRC_LOOKS_LIKE_UPC: {
    en: () =>
      "It is all digits: it looks like a barcode (UPC/EAN), not an ISRC. An ISRC starts with 2 letters, for example AR-F03-97-03368.",
    pt: () =>
      "São só números: parece um código de barras (UPC/EAN), não um ISRC. O ISRC começa com 2 letras, por exemplo AR-F03-97-03368.",
  },
  ISRC_LETTER_IN_DIGITS: {
    en: (es) =>
      `Positions 6 to 12 take digits only: is ${lettersForDigits(es, "the O a zero", "the I a one", "and")}?`,
    pt: (es) =>
      `As posições 6 a 12 levam só números: ${lettersForDigits(es, "o O é um zero", "o I é um um", "e")}?`,
  },
  ISRC_RESERVED_PREFIX: {
    en: (es) => {
      const prefix = detail(es, /^El prefijo (\S+) es/);
      return `${prefix ? `The prefix ${prefix}` : "This prefix"} is an IFPI example and is never assigned to anyone.`;
    },
    pt: (es) => {
      const prefix = detail(es, /^El prefijo (\S+) es/);
      return `${prefix ? `O prefixo ${prefix}` : "Este prefixo"} é um exemplo da IFPI e nunca é atribuído a ninguém.`;
    },
  },
  ISRC_TC_RETIRED: {
    en: () =>
      "Agency code TC (the one TuneCore used) cannot be used with reference year 26 or later.",
    pt: () =>
      "O código de agência TC (o que a TuneCore usava) não pode ser usado com ano de referência 26 ou posterior.",
  },
  ISRC_UNKNOWN_AGENCY: {
    en: (es) => {
      const agency = detail(es, /^El código de agencia (\S+) no/);
      return `${agency ? `Agency code ${agency}` : "The agency code"} is not in the IFPI list (${IFPI_AGENCY_CODES_DATE}). Check the ISRC; if the code is new, it can be saved anyway.`;
    },
    pt: (es) => {
      const agency = detail(es, /^El código de agencia (\S+) no/);
      return `${agency ? `O código de agência ${agency}` : "O código de agência"} não está na lista da IFPI (${IFPI_AGENCY_CODES_DATE}). Revise o ISRC; se o código for novo, pode ser salvo mesmo assim.`;
    },
  },
  ISWC_MISSING_T: {
    en: (es) =>
      `The initial T is missing: an ISWC is a T followed by 10 digits.${iswcSuggestion(es, "Did you mean")}`,
    pt: (es) =>
      `Falta o T inicial: o ISWC é um T seguido de 10 dígitos.${iswcSuggestion(es, "Você quis dizer")}`,
  },
  ISWC_WRONG_PREFIX: {
    en: (es) => {
      const letter = detail(es, /no con la (\S)\.$/);
      return `An ISWC starts with the letter T${letter ? `, not ${letter}` : ""}.`;
    },
    pt: (es) => {
      const letter = detail(es, /no con la (\S)\.$/);
      return `O ISWC começa com a letra T${letter ? `, não com ${letter}` : ""}.`;
    },
  },
  ISWC_MISSING_CHECK_DIGIT: {
    en: () => "The check digit is missing: an ISWC is a T followed by 10 digits.",
    pt: () => "Falta o dígito verificador: o ISWC é um T seguido de 10 dígitos.",
  },
  ISWC_LETTER_IN_DIGITS: {
    en: (es) => `An ISWC has only digits after the T.${iswcSuggestion(es, "Did you mean")}`,
    pt: (es) => `O ISWC só leva números depois do T.${iswcSuggestion(es, "Você quis dizer")}`,
  },
  ISWC_FORMAT: {
    en: () => "An ISWC is a T followed by 10 digits (for example T-034.524.680-1).",
    pt: () => "O ISWC é um T seguido de 10 dígitos (por exemplo T-034.524.680-1).",
  },
  ISWC_ZERO_WORK: {
    en: () => "The work number cannot be 000000000.",
    pt: () => "O número da obra não pode ser 000000000.",
  },
  ISWC_CHECK_DIGIT: {
    en: () => "The check digit does not match. Check the digits against the original source.",
    pt: () => "O dígito verificador não confere. Revise os números com a fonte original.",
  },
  IPI_BASE_NUMBER: {
    en: () =>
      "It is an IPI Base Number (I-NNNNNNNNN-C), which identifies the person. This field takes the IPI Name Number: 11 digits.",
    pt: () =>
      "É um IPI Base Number (I-NNNNNNNNN-C), que identifica a pessoa. Aqui vai o IPI Name Number: 11 dígitos.",
  },
  IPI_FORMAT: {
    en: () => "An IPI has digits only: the 11 digits, or the 9 of the CAE.",
    pt: () => "O IPI leva só números: os 11 dígitos, ou os 9 do CAE.",
  },
  IPI_TOO_LONG: {
    en: (es) => {
      const count = detail(es, /^Tiene (\d+) dígitos/);
      return `It has ${count ?? "more than 11"} digits and an IPI has 11: is it an ISNI?`;
    },
    pt: (es) => {
      const count = detail(es, /^Tiene (\d+) dígitos/);
      return `Tem ${count ?? "mais de 11"} dígitos e o IPI tem 11: é um ISNI?`;
    },
  },
  IPI_ALL_ZEROS: {
    en: () => "An IPI cannot be all zeros.",
    pt: () => "O IPI não pode ser só zeros.",
  },
  IPI_CHECK_DIGITS: {
    en: () =>
      "The check digits do not match. Check the number against the source (SADAIC or CISAC).",
    pt: () =>
      "Os dígitos de controle não conferem. Revise o número com a fonte (SADAIC ou CISAC).",
  },
};

/** El texto de un error o aviso en el idioma pedido. */
export function messageFor(issue: IdentifierIssue, lang: IdentifierLanguage): string {
  if (lang === "es") return issue.message;
  const translate = TEXTS[issue.code]?.[lang];
  return translate ? translate(issue.message) : issue.message;
}
